import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { CHANNEL_LABEL, fmtChatDay, fmtDateTime, fmtTime, localDate, get, html, LOST_REASON_LABEL, navigate, patch, post, relTime, STAGES, stageLabel, STATUS_LABEL } from "../lib.js";
import { locale, tr } from "../i18n.js";
import { Badge, Empty, ErrorBox, Loading, PageHeader, useAction, useLoad, usePoll } from "../ui.js";
import { ContactIntake } from "./intake.js";
import { ContactTasks } from "./tasks.js";
import { PageGuide } from "../guide.js";

const pingAttention = () => window.dispatchEvent(new Event("rz:attention"));

const FILTERS = [
  { key: "team", label: "Needs action now" },
  { key: "customer", label: "Waiting for customer" },
  { key: "overdue", label: "Response overdue" },
  { key: "snoozed", label: "Snoozed" },
  { key: "all", label: "All" },
  { key: "ai", label: "AI handling", status: true },
  { key: "human", label: "Needs a person", status: true },
  { key: "closed", label: "Closed", status: true },
];

const statusKind = { ai: "ok", human: "warn", closed: "neutral" };

/** Display labels for booking statuses (the stored value stays English). */
const bookingStatusLabel = (s) =>
  ({ pending_payment: tr("awaiting deposit"), confirmed: tr("confirmed"), completed: tr("completed"), no_show: tr("no-show"), cancelled: tr("cancelled") })[s] ?? s;

export function InboxPage({ t, selectedId }) {
  const [filter, setFilter] = useState("team");
  const [owner, setOwner] = useState("");
  const [settingsBusy, settingsRun] = useAction();
  const [q, setQ] = useState("");
  const [list, reloadList, error] = useLoad(
    () => get(`${t.base}/conversations?${new URLSearchParams({ ...(FILTERS.find((f) => f.key === filter)?.status ? { status: filter } : { view: filter }), ...(owner && { owner }), ...(q && { q }) })}`),
    [t.id, filter, owner, q],
  );
  usePoll(useCallback(reloadList, [filter, owner, q]), 5000);

  return html`<div class="inbox ${selectedId ? "has-selection" : ""}">
    <section class="inbox-list">
      <${PageHeader} title=${tr("Inbox")} />
      <${PageGuide} id="inbox" />
      <div class="tabs" role="tablist">
        ${FILTERS.map((f) => html`<button role="tab" aria-selected=${filter === f.key} class=${filter === f.key ? "active" : ""} onClick=${() => setFilter(f.key)}>
          ${tr(f.label)}${(f.status ? list?.counts?.[f.key] : list?.views?.[f.key]) > 0 ? html` <span class="count">${f.status ? list.counts[f.key] : list.views[f.key]}</span>` : ""}
        </button>`)}
      </div>
      <div class="inbox-tools">
        <label class="field"><span class="label">${tr("Owner")}</span><select value=${owner} onChange=${(e) => setOwner(e.target.value)}>
          <option value="">${tr("Everyone")}</option><option value="me">${tr("Assigned to me")}</option><option value="unassigned">${tr("Unassigned")}</option>
        </select></label>
        ${list && html`<label class="field"><span class="label">${tr("Response target")}</span><select aria-label=${tr("Response target")} disabled=${settingsBusy || t.role === "agent"} value=${list.response_target_minutes}
          onChange=${async (e) => { await settingsRun(() => patch(`${t.base}/inbox/settings`, { human_response_minutes: Number(e.target.value) }), tr("Saved")); reloadList(); }}>
          ${[...new Set([15, 30, 60, 120, 240, 1440, list.response_target_minutes])].sort((a, b) => a - b).map((n) => html`<option value=${n}>${tr("{n} minutes", { n })}</option>`)}
        </select></label>`}
      </div>
      <p class="muted small">${tr("Response time is elapsed time, including closed hours. Snoozing hides the chat until it is due or the customer writes again.")}</p>
      ${list?.views?.overdue > 0 && html`<div class="callout warn small" role="status">${tr("{n} chats passed the response target", { n: list.views.overdue })}</div>`}
      <input type="search" class="search" placeholder=${tr("Search name or phone")} value=${q} onInput=${(e) => setQ(e.target.value)} />
      ${error && html`<${ErrorBox} error=${error} />`}${!list && !error && html`<${Loading} />`}
      ${list && !list.conversations.length && html`<${Empty} title=${tr("No conversations")}>${tr("Messages from your channels will appear here.")}</${Empty}>`}
      <ul class="conv-list">
        ${list?.conversations.map((c) => html`<li>
          <a href=${`/inbox/${c.id}`} class="conv ${c.id === selectedId ? "active" : ""}">
            <div class="conv-top">
              <strong>${c.contact_name || c.contact_phone || tr("Visitor")}</strong>
              <span class="muted small">${relTime(c.last_message_at)}</span>
            </div>
            <div class="conv-snippet muted" dir="auto">${c.last_body ?? ""}</div>
            <div class="conv-tags">${c.waiting_on === "team" && html`<${Badge} kind=${c.overdue ? "warn" : "neutral"}>${c.overdue ? tr("Response overdue") : tr("Waiting for team")}</${Badge}>`}
              ${c.waiting_on === "customer" && html`<${Badge} kind="neutral">${tr("Waiting for customer")}</${Badge}>`}
              ${!!c.snoozed && html`<${Badge} kind="neutral">${tr("Snoozed")}</${Badge}>`} <${Badge} kind=${statusKind[c.status]}>${STATUS_LABEL[c.status]}</${Badge}> ${c.has_draft ? html`<${Badge} kind="warn">${tr("Reply waiting")}</${Badge}> ` : ""}<span class="muted small">${CHANNEL_LABEL[c.channel_type]} · ${stageLabel(c.contact_stage)}</span></div>
            <div class="muted small">${c.owner_name || tr("Unassigned")}${c.waiting_on === "team" ? ` · ${tr("Waiting since {time}", { time: fmtDateTime(c.waiting_since, t.timezone) })}` : ""}</div>
          </a>
        </li>`)}
      </ul>
    </section>
    <section class="inbox-thread">
      ${selectedId ? html`<${Thread} key=${t.id + ":" + selectedId} t=${t} id=${selectedId} onChange=${reloadList} />` : html`<${Empty} title=${tr("Pick a conversation")}>${tr('The AI answers on its own. Conversations that need a person show up under "Needs a person".')}</${Empty}>`}
    </section>
  </div>`;
}

function Thread({ t, id, onChange }) {
  const [data, reload, error] = useLoad(() => get(`${t.base}/conversations/${id}`), [id]);
  usePoll(reload, 4000);
  const [text, setText] = useState("");
  const [busy, run] = useAction();
  const bottom = useRef(null);
  const count = data?.messages.length ?? 0;
  useEffect(() => bottom.current?.scrollIntoView({ block: "end" }), [count]);

  if (error) return html`<${Empty} title=${tr("Conversation not found")} />`;
  if (!data) return html`<${Loading} />`;
  const { conversation: cv, contact, channel, messages, bookings, draft } = data;

  const act = async (action, msg) => {
    await run(() => post(`${t.base}/conversations/${id}/${action}`), msg);
    reload();
    onChange();
    pingAttention();
  };
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await run(() => post(`${t.base}/conversations/${id}/reply`, { text }));
    setText("");
    reload();
    onChange();
  };

  return html`<div class="thread">
    <header class="thread-head">
      <button class="icon-btn back" aria-label=${tr("Back")} onClick=${() => navigate("/inbox")}>${tr("←")}</button>
      <div>
        <h2>${contact.name || contact.phone || tr("Visitor")}</h2>
        <div class="muted small">${CHANNEL_LABEL[channel.type]}${contact.phone ? ` · ${contact.phone}` : ""} · <${Badge} kind=${statusKind[cv.status]}>${STATUS_LABEL[cv.status]}</${Badge}></div>
        ${cv.status === "human" && cv.handoff_reason && html`<div class="callout warn small">${tr("Handed off: {reason}", { reason: cv.handoff_reason })}</div>`}
      </div>
      <div class="actions">
        ${cv.status === "ai" && html`<button class="btn" disabled=${busy} onClick=${() => act("takeover", tr("You're handling this chat"))}>${tr("Take over")}</button>`}
        ${cv.status !== "ai" && html`<button class="btn primary" disabled=${busy} onClick=${() => act("release", tr("AI is handling this chat again"))}>${tr("Return to AI")}</button>`}
        ${cv.status !== "closed" && html`<button class="btn ghost" disabled=${busy} onClick=${() => act("close", tr("Closed"))}>${tr("Close")}</button>`}
        ${channel.type === "whatsapp" && !!channel.personal_shield && t.role !== "agent" && html`<button class="btn ghost" disabled=${busy} onClick=${() => { if (confirm(tr("Mark this number as personal? The AI stops answering it, nothing more from it is stored, and the chat closes. You can undo it under Channels → Known numbers."))) act("personal", tr("Marked personal")); }}>${tr("This is personal, stop the AI here")}</button>`}
      </div>
    </header>
    ${cv.status === "human" && html`<${HandoffCard} t=${t} data=${data} busy=${busy} run=${run} onDone=${() => { reload(); onChange(); pingAttention(); }} />`}
    <div class="thread-body">
      <div class="messages">
        ${messages.map((m, i) => html`${(i === 0 || localDate(new Date(m.created_at), t.timezone) !== localDate(new Date(messages[i - 1].created_at), t.timezone)) && html`<div class="day-divider"><span>${fmtChatDay(m.created_at, t.timezone)}</span></div>`}
        <div id=${`message-${m.id}`} class="msg ${m.direction} ${m.author}">
          <div class="bubble" dir="auto">${m.media_type && html`<${Attachment} src=${`/api${t.base}/conversations/${id}/messages/${m.id}/media`} type=${m.media_type} />`}${messageBody(m.body)}</div>
          <div class="meta" title=${fmtDateTime(m.created_at, t.timezone)}>${m.author === "contact" ? "" : `${m.author === "agent" ? tr("AI") : m.author === "staff" ? tr("Team") : tr("Automated")} · `}${fmtTime(m.created_at, t.timezone)}
            ${m.status === "failed" && html` · <span class="error-text" title=${m.error}>${tr("not delivered")}</span>`}
            ${m.status === "read" && ` · ${tr("read")}`}</div>
        </div>`)}
        <div ref=${bottom}></div>
      </div>
      <${ContactPanel} t=${t} contact=${contact} bookings=${bookings} onSaved=${() => { reload(); onChange(); }} />
    </div>
    ${draft && html`<${DraftBox} key=${draft.id} t=${t} conversationId=${id} draft=${draft} onDone=${() => { reload(); onChange(); }} />`}
    <form class="composer" onSubmit=${send}>
      <textarea dir="auto" rows="2" placeholder=${cv.status === "ai" ? tr("Type to reply; sending takes the chat over from the AI") : tr("Type your reply")} value=${text}
        onInput=${(e) => setText(e.target.value)} onKeyDown=${(e) => e.key === "Enter" && !e.shiftKey && send(e)}></textarea>
      <button class="btn primary" disabled=${busy || !text.trim()}>${tr("Send")}</button>
    </form>
  </div>`;
}

/** Source excerpts and existing CRM actions, not a model-generated claim about what happened. */
function HandoffCard({ t, data, busy, run, onDone }) {
  const { conversation: cv, contact, messages, workflow: w, open_tasks: tasks = [] } = data;
  const lastRequest = [...messages].reverse().find((m) => m.author === "contact");
  const actions = messages.filter((m) => m.direction === "out" && m.status !== "failed" && ["staff", "agent"].includes(m.author)).slice(-2);
  const change = async (fields) => {
    await run(() => patch(`${t.base}/conversations/${cv.id}/workflow`, fields), tr("Saved"));
    onDone();
  };
  return html`<section class="handoff-card" aria-label=${tr("Handoff context")}>
    <details open>
      <summary><strong>${tr("Handoff context")}</strong> · ${w?.owner_name || tr("Unassigned")}</summary>
      ${lastRequest && html`<p class="small"><strong>${tr("Latest customer request")}: </strong><a dir="auto" href=${`#message-${lastRequest.id}`}>${lastRequest.body.slice(0, 350)}</a></p>`}
      ${cv.handoff_reason && html`<p class="small"><strong>${tr("Reason for handoff")}: </strong><span dir="auto">${cv.handoff_reason}</span></p>`}
      ${actions.length > 0 && html`<p class="small"><strong>${tr("Recent replies")}: </strong>${actions.map((m) => html`<a class="handoff-excerpt" dir="auto" href=${`#message-${m.id}`}>${m.body.slice(0, 220)}</a>`)}</p>`}
      <p class="small"><strong>${tr("Next step")}: </strong>${tasks.length ? html`<a href="/tasks">${tasks[0].title} · ${fmtDateTime(tasks[0].due_at, t.timezone)}</a>` : !contact.assigned_user_id ? tr("Assign an owner, then review the request and reply.") : w?.waiting_on === "customer" ? tr("Wait for the customer, or set a follow-up task.") : tr("Review the request and reply, or set a follow-up task.")}</p>
      ${tasks.length > 1 && html`<p class="small"><a href="/tasks">${tr("{n} open follow-up tasks", { n: tasks.length })}</a></p>`}
    </details>
    <div class="inbox-workflow-controls">
      <label class="field"><span class="label">${tr("Waiting on")}</span><select value=${cv.inbox_waiting_on} disabled=${busy} onChange=${(e) => change({ waiting_on: e.target.value })}>
        <option value="auto">${tr("Detect from replies")}</option><option value="team">${tr("Waiting for team")}</option><option value="customer">${tr("Waiting for customer")}</option>
      </select></label>
      <label class="field"><span class="label">${tr("Snooze")}</span><select value="" disabled=${busy} onChange=${(e) => { if (e.target.value) change({ snooze_minutes: Number(e.target.value) }); }}>
        <option value="">${tr("Remind me in…")}</option><option value="60">${tr("1 hour")}</option><option value="180">${tr("3 hours")}</option><option value="1440">${tr("24 hours")}</option>
      </select></label>
      ${!!w?.snoozed && html`<button class="btn small" disabled=${busy} onClick=${() => change({ snooze_minutes: null })}>${tr("Resume now")}</button>`}
    </div>
    ${!!w?.snoozed && html`<p class="muted small">${tr("Snoozed until {time}", { time: fmtDateTime(w.inbox_snoozed_until, t.timezone) })}</p>`}
    ${w?.waiting_on === "team" && html`<p class=${`small ${w.overdue ? "error-text" : "muted"}`} role="status">${w.overdue ? tr("Response overdue") : tr("Waiting for team")} · ${tr("Waiting since {time}", { time: fmtDateTime(w.waiting_since, t.timezone) })}</p>`}
  </section>`;
}

/**
 * Draft mode: the agent's reply, time picker and any booking change, held until a person sends or
 * discards them. Texts can be rewritten first; a rewritten text goes out as the team's message.
 */
function DraftBox({ t, conversationId, draft, onDone }) {
  const [edits, setEdits] = useState({});
  const [busy, run] = useAction();
  const waiting = draft.status === "pending";
  const failed = draft.status === "failed";
  const decide = async (action) => {
    const changed = Object.fromEntries(Object.entries(edits).filter(([i, v]) => v !== draft.parts[i]?.text));
    await run(
      () => post(`${t.base}/conversations/${conversationId}/drafts/${draft.id}/${action}`, action === "send" ? { edits: changed } : undefined),
      action === "send" ? tr("Sending…") : tr("Reply discarded"),
    );
    onDone();
    pingAttention();
  };
  return html`<section class="draft-box callout">
    <strong>${waiting ? tr("Reply waiting for your approval") : failed ? tr("This reply wasn't sent") : tr("Sending…")}</strong>
    ${failed && draft.error && html`<p class="small error-text" dir="auto">${draft.error}</p>`}
    ${draft.parts.map((p, i) => html`<div class="draft-part">
      <textarea dir="auto" rows="3" disabled=${!waiting || busy} value=${edits[i] ?? p.text} onInput=${(e) => setEdits({ ...edits, [i]: e.target.value })}></textarea>
      ${p.kind === "choices" && html`<div class="chips">${p.choices.map((c) => html`<span class="chip">${c.title}</span>`)}</div>`}
    </div>`)}
    ${draft.actions.length > 0 && html`<p class="small"><strong>${tr("Sending also does this:")}</strong></p>
      <ul class="small">${draft.actions.map((a) => html`<li dir="auto">${a.summary}</li>`)}</ul>
      <p class="muted small">${tr("The agent checks it is still possible when you send, then confirms with the customer.")}</p>`}
    ${waiting && html`<div class="row-actions">
      <button class="btn primary" disabled=${busy} onClick=${() => decide("send")}>${tr("Send")}</button>
      <button class="btn ghost" disabled=${busy} onClick=${() => decide("discard")}>${tr("Discard")}</button>
    </div>`}
    ${failed && html`<div class="row-actions"><button class="btn ghost" disabled=${busy} onClick=${() => decide("discard")}>${tr("Dismiss")}</button></div>`}
  </section>`;
}

function ContactPanel({ t, contact, bookings, onSaved }) {
  const [notes, setNotes] = useState(contact.notes ?? "");
  const [busy, run] = useAction();
  const [members] = useLoad(() => get(`${t.base}/members`), [t.id]);
  const save = (fields, msg = tr("Saved")) => run(() => patch(`${t.base}/contacts/${contact.id}`, fields), msg).then(onSaved);
  return html`<aside class="contact-panel">
    <h3>${tr("Customer")}</h3>
    <label class="field"><span class="label">${tr("Stage")}</span>
      <select value=${contact.stage} onChange=${(e) => save({ stage: e.target.value })}>${STAGES.map((s) => html`<option value=${s}>${stageLabel(s)}</option>`)}</select>
    </label>
    ${contact.stage === "lost" && html`<label class="field"><span class="label">${tr("Lost because")}</span>
      <select value=${contact.lost_reason ?? ""} onChange=${(e) => save({ lost_reason: e.target.value || null })}>
        <option value="">${tr("Not recorded")}</option>
        ${Object.entries(LOST_REASON_LABEL).map(([k, v]) => html`<option value=${k}>${tr(v)}</option>`)}
      </select></label>`}
    <label class="field"><span class="label">${tr("Owner")}</span>
      <select value=${contact.assigned_user_id ?? ""} onChange=${(e) => save({ assigned_user_id: e.target.value || null }, tr("Owner updated"))}>
        <option value="">${tr("Unassigned")}</option>
        ${members?.members.map((m) => html`<option value=${m.id}>${m.name}</option>`)}
      </select></label>
    ${contact.interest && html`<p class="small"><span class="muted">${tr("Interested in:")}</span> ${contact.interest}</p>`}
    ${contact.email && html`<p class="small"><span class="muted">${tr("Email:")}</span> ${contact.email}</p>`}
    ${contact.source && contact.source !== "direct" && html`<p class="small"><span class="muted">${tr("Came from:")}</span> ${contact.source}${contact.campaign ? ` · ${contact.campaign}` : ""}</p>`}
    ${contact.opted_out
      ? html`<p class="small"><${Badge} kind="warn">${tr("Opted out of marketing")}</${Badge}></p>`
      : html`<label class="toggle small" title=${contact.marketing_opt_in_at ? tr("Recorded {when} ({source})", { when: new Date(contact.marketing_opt_in_at).toLocaleString(locale()), source: contact.marketing_opt_in_source }) : ""}>
          <input type="checkbox" checked=${!!contact.marketing_opt_in} disabled=${busy} onChange=${(e) => save({ marketing_opt_in: e.target.checked }, e.target.checked ? tr("Consent recorded") : tr("Consent removed"))} />
          ${tr("Agreed to receive offers")}</label>`}
    <label class="field"><span class="label">${tr("Notes")}</span><textarea rows="4" value=${notes} onInput=${(e) => setNotes(e.target.value)}></textarea></label>
    <button class="btn small" disabled=${busy || notes === (contact.notes ?? "")} onClick=${() => save({ notes })}>${tr("Save notes")}</button>
    <${ContactIntake} key=${t.id + ":" + contact.id} t=${t} contactId=${contact.id} />
    <h3>${tr("Tasks")}</h3>
    <${ContactTasks} t=${t} contactId=${contact.id} onChange=${pingAttention} />
    <h3>${tr("Appointments")}</h3>
    ${bookings.length
      ? html`<ul class="plain">${bookings.map((b) => html`<li class="small"><strong>${b.service_name}</strong><br />${fmtDateTime(b.start_at, t.timezone)} <${Badge} kind=${b.status === "confirmed" ? "ok" : "neutral"}>${bookingStatusLabel(b.status)}</${Badge}>${b.is_test ? html` <${Badge} kind="warn">${tr("Test booking")}</${Badge}>` : ""}</li>`)}</ul>`
      : html`<p class="muted small">${tr("None yet.")}</p>`}
  </aside>`;
}

/**
 * What the customer attached, fetched through the API from Meta. Meta keeps files for a limited
 * time, so an old one shows a note instead of a broken image.
 */
function Attachment({ src, type }) {
  const [gone, setGone] = useState(false);
  if (gone) return html`<div class="muted small">${tr("The file is no longer available.")}</div>`;
  if (type === "image") return html`<a href=${src} target="_blank" rel="noopener"><img class="msg-media" src=${src} alt=${tr("Photo from the customer")} loading="lazy" onError=${() => setGone(true)} /></a>`;
  if (type === "video") return html`<video class="msg-media" src=${src} controls preload="metadata" onError=${() => setGone(true)}></video>`;
  return html`<a class="small" href=${src} target="_blank" rel="noopener">📎 ${tr("Open the file")}</a>`;
}

const TRANSCRIPT = "[Voice message, transcribed automatically] ";
/** Voice notes arrive as transcripts; label them so staff know the words came from speech-to-text. */
function messageBody(body) {
  if (!body.startsWith(TRANSCRIPT)) return body;
  return html`<span class="muted small">🎙 ${tr("Voice note, transcribed")}</span><br />${body.slice(TRANSCRIPT.length)}`;
}


